import { FormPublicFieldFragment } from '../../../../graphql/fragment/form.public.fragment';
import { Typography, Grid, Stack, Box } from '@mui/material';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';

interface UserAnswer {
  field: string; // question id
  data: string; // user chosen question answer
}

export const generateAnswersTable = (
  questions: FormPublicFieldFragment[],
  pillars: Array<string>,
  userAnswers: Array<UserAnswer>,
  mobile: boolean
) => {
  const focusAreaLevels = ['Lagging', 'Sustainable', 'Leading'];
  const pillarGridItemClass = {
    backgroundColor: '#86BC25',
    padding: '20px',
  };
  const pillarClass = {
    whiteSpace: 'pre-line',
    fontSize: '1.5rem',
    fontWeight: 'bold',
  };
  /* 
      on desktop, a "row" on the grid is rendered as it sounds like
      row 0 - v&s, t&o, f&o, d&m
      row 1 - focusAreaClasses[0], focusAreaClasses[1], focusAreaClasses[2], focusAreaClasses[3], 
      row 2 - 0, 1, 2, 3
      row 3 - 0, 1, 2, 3
      row 4 - 0, 1, 2, 3

      on mobile, a "row" on the grid is rendered as a pillar's column
      row 0 - v&s
              focusAreaClasses[0]
              focusAreaClasses[1]
              focusAreaClasses[2]
              focusAreaClasses[3]
      
      row 1 - t&o
              0
              1
              2
              3
      etc
    */
  const focusAreaClasses = [
    { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    { backgroundColor: 'white' },
    { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    { backgroundColor: 'white' },
  ];
  // lagging, sustainable, leading chip colors
  const focusAreaChipClasses = [
    {
      backgroundColor: '#FFCCCC',
      color: 'rgba(133, 0, 0, 0.87)',
      border: '1px solid rgba(133, 0, 0, 0.87)',
    },
    {
      backgroundColor: '#FAF3B9',
      color: 'rgba(255, 152, 0, 1)',
      border: '1px solid rgba(255, 152, 0, 1)',
    },
    {
      backgroundColor: '#E8F9F1',
      color: 'rgba(0, 111, 59, 1)',
      border: '1px solid rgba(0, 111, 59, 1)',
    },
  ];
  const tableItems: Array<any> = [<></>, <></>, <></>, <></>, <></>];
  /*
      firstCellIndex - starting point of the column or row in the answers table
      starting from question 5 to +3, those questions belong into a specific pillar and the table should look like this:

      vision & strategy -          5 6 7 8
      talent & organization -      9 10 11 12
      focus on users & outcomes -  13 14 15 16
      delivery & mindset -         17 18 19 20

      on mobile, we render each pillar with its questions as its own row since mobile do not have space for 4 "wide" rows
      thus, we want each pillar (ex vision & strategy) to have all it's focus areas (ex 5 6 7 8) in a row
      and the table should look like this:

      V&S scroll down ->  T&O scroll down ->  F&O scroll down ->  D&M
      5                   9                   13                  17
      6                   10                  14                  18
      7                   11                  15                  19
      8                   12                  16                  20
      
      in this case, the answers table has 4 rows that are really "tall but short width wise"

      on desktop, we have space for long rows (thus giving the appearance of 4 "columns") 
      thus we render firstCells of each category as its its own row, secondCells as own row, etc
      row 1   V&S T&O F&O D&M
      row 2   5   9   13  17
      row 3   6   10  14  18
      row 4   7   11  15  19
      row 5   8   12  16  20

      in this case, the answers table has 5 (1 row for pillars) rows that are really "wide but short height wise"
    */
  for (let i = 0; i < 4; i++) {
    const firstCellIndex = mobile ? i * 4 + 5 : i + 5;
    const cellsArray: Array<any> = [<></>, <></>, <></>, <></>];
    for (let j = 0; j < 4; j++) {
      const currentFocusArea = mobile
        ? j + firstCellIndex
        : j * 4 + firstCellIndex;
      const userAnswerValue: number =
        parseInt(userAnswers[currentFocusArea].data.replace('"', '')) - 1; // 0, 1, or 2
      const userFocusAreaLevel = focusAreaLevels[userAnswerValue];
      const focusAreaChipClass = focusAreaChipClasses[userAnswerValue];
      const cell = (
        <Grid
          item
          xs={mobile ? undefined : 3}
          sx={{
            padding: '20px',
            ...focusAreaClasses[j],
          }}
        >
          <Stack direction='row' sx={{ alignItems: 'center' }}>
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: '#8DB821',
                marginRight: '10px',
              }}
            >
              {`${questions[currentFocusArea].title}`}
            </Typography>
            <Typography
              sx={{
                ...focusAreaChipClass,
                fontSize: '0.75rem',
                borderRadius: '16px',
                display: 'inline-block',
                padding: '4px 8px',
              }}
            >
              {userFocusAreaLevel}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '0.8rem' }}>
            {questions[currentFocusArea].options[userAnswerValue].title}
          </Typography>
        </Grid>
      );
      cellsArray[j + 1] = cell;
    }

    // process to add a row to render
    if (mobile) {
      cellsArray[0] = (
        <Grid item sx={pillarGridItemClass}>
          <Typography align='center' sx={pillarClass}>
            {pillars[i]}
          </Typography>
        </Grid>
      );

      const row = (
        <Grid key={`pillar-row-${i}`} item>
          {cellsArray[0]}
          {cellsArray[1]}
          {cellsArray[2]}
          {cellsArray[3]}
          {cellsArray[4]}
        </Grid>
      );
      tableItems[i] = row;
    } else {
      const row = (
        <Grid key={`pillar-row-${i + 1}`} container>
          {cellsArray[1]}
          {cellsArray[2]}
          {cellsArray[3]}
          {cellsArray[4]}
        </Grid>
      );
      tableItems[i + 1] = row;
    }
  }

  if (!mobile) {
    const pillarsArray = [<></>, <></>, <></>, <></>];
    for (let i = 0; i < 4; i++) {
      pillarsArray[i] = (
        <Grid key={`pillar-griditem-${i}`} item xs={3} sx={pillarGridItemClass}>
          <Typography align='center' sx={pillarClass}>
            {pillars[i]}
          </Typography>
        </Grid>
      );
    }
    const pillarsRow = (
      <Grid key={`pillar-row-0`} container>
        {pillarsArray[0]}
        {pillarsArray[1]}
        {pillarsArray[2]}
        {pillarsArray[3]}
      </Grid>
    );
    tableItems[0] = pillarsRow;
  }

  return tableItems;
};

export const generateTableLegend = (descriptions: string[]) => {
  const colors = [
    {
      color: 'rgba(146, 0, 0, 1)',
      backgroundColor: 'rgba(255, 204, 204, 1)',
    },
    {
      color: 'rgba(255, 152, 0, 1)',
      backgroundColor: 'rgba(250, 243, 185, 1)',
    },
    {
      color: 'rgba(0, 111, 59, 1)',
      backgroundColor: 'rgba(232, 249, 241, 1)',
    },
  ];
  const scoring = [
    <>
      <Typography align='center' sx={{ marginRight: '2px' }}>
        Lagging
      </Typography>
      <SentimentVeryDissatisfiedIcon />
    </>,
    <>
      <Typography align='center' sx={{ marginRight: '2px' }}>
        Sustainable
      </Typography>
      <SentimentNeutralIcon />
    </>,
    <>
      <Typography align='center' sx={{ marginRight: '2px' }}>
        Leading
      </Typography>
      <SentimentVerySatisfiedIcon />
    </>,
  ];
  const urgencies = [
    <>
      Requires <strong>immediate </strong>attention
    </>,
    'Needs to be addressed in the near-term',
    'Continue to improve in long-term transformations',
  ];
  const effectivenesses = [
    'Characterized by inefficacy',
    'Organization is functioning, but not at an optimal level​',
    'Displaying operational excellence​',
  ];
  const scalabilities = [
    <>
      <strong>Completely unscalable. </strong>Scale risks foundational failure
    </>,
    'Difficult to scale. Scale would further expose weaknesses and add pressure on teams​',
    'Scalable with few concerns ',
  ];

  const scoringCells = [<></>, <></>, <></>];
  for (let i = 0; i < 3; i++) {
    const cell = (
      <Box
        sx={{
          border: `2px solid ${colors[i].color}`,
          backgroundColor: 'white',
          height: ['auto', 'auto', '100%'],
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            color: colors[i].color,
            backgroundColor: colors[i].backgroundColor,
            padding: '10px 0',
          }}
        >
          {scoring[i]}
        </Box>
        <Box sx={{ padding: '20px' }}>
          <Typography
            align='center'
            sx={{ fontSize: '0.8rem', marginBottom: '20px' }}
          >
            {descriptions[i]}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', margin: '10px 0' }}>
            <strong style={{ color: '#8DB821' }}>URGENCY: </strong>
            {urgencies[i]}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', margin: '10px 0' }}>
            <strong style={{ color: '#8DB821' }}>
              OPERATIONAL EFFECTIVENESS:{' '}
            </strong>
            {effectivenesses[i]}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', margin: '10px 0' }}>
            <strong style={{ color: '#8DB821' }}>SCALABILITY: </strong>
            {scalabilities[i]}
          </Typography>
        </Box>
      </Box>
    );
    scoringCells[i] = cell;
  }

  return (
    <Grid container columns={{ xs: 12, md: 92 }}>
      <Grid item xs={12} md={30} sx={{ width: ['100%', '100%', 'auto'] }}>
        {scoringCells[0]}
      </Grid>
      <Grid item xs={0} md={1}></Grid>
      <Grid
        item
        xs={12}
        md={30}
        sx={{
          width: ['100%', '100%', 'auto'],
          margin: ['20px 0', '20px 0', '0'],
        }}
      >
        {scoringCells[1]}
      </Grid>
      <Grid item xs={0} md={1}></Grid>
      <Grid item xs={12} md={30} sx={{ width: ['100%', '100%', 'auto'] }}>
        {scoringCells[2]}
      </Grid>
    </Grid>
  );
};
